import {inject, injectable} from "inversify";
import {Team} from "@/modules/team/entity";
import {StaffMember} from "@/modules/staff/entity";
import {BankCategory} from "@/modules/finances/enums";
import {GateTypeEnum} from "@/modules/action-points/enums";
import GateManager from "@/modules/action-points/services/GateManager";
import {EventEmitter} from "@/modules/base/events";
import {BeforeSackStaffMemberEvent} from "@/modules/staff/events/BeforeSackStaffMemberEvent";
import {AfterSackStaffMemberEvent} from "@/modules/staff/events/AfterSackStaffMemberEvent";

@injectable()
export class StaffMemberTransferator {
  constructor(
    @inject(EventEmitter) private em: EventEmitter,
    @inject(GateManager) private gateManager: GateManager,
  ) {
  }

  public async sackForFree(staffMember: StaffMember) {
    await staffMember.load('team');

    if (!staffMember.team) {
      throw 'Staff member has no team';
    }

    await this.em.emitAsync(new BeforeSackStaffMemberEvent(staffMember, 0));

    await staffMember.remove();

    await this.em.emitAsync(new AfterSackStaffMemberEvent(staffMember, 0));
  }

  public async sack(staffMember: StaffMember) {
    await staffMember.load('team');

    if (!staffMember.team) {
      throw 'Staff member has no team';
    }

    const price = staffMember.sackPrice;

    await this.em.emitAsync(new BeforeSackStaffMemberEvent(staffMember, price));

    if (staffMember.team.user) {
      await this.gateManager.getGate(GateTypeEnum.BALANCE).currency.subtract(
        price,
        {
          category: BankCategory.TRANSFERS,
          label: `Sack staff member ${staffMember.name}`,
        }
      );
    }

    await staffMember.remove();

    await this.em.emitAsync(new AfterSackStaffMemberEvent(staffMember, price));
  }

  public canTransfer(staffMember: StaffMember, team: Team) {
    return team.staffMembers.length < team.staffSlots;
  }

  public async transfer(staffMember: StaffMember, team: Team, price?: number) {
    if (!this.canTransfer(staffMember, team)) {
      throw 'Can not transfer staff to team.';
    }

    if (team.user) {
      await this.gateManager.getGate(GateTypeEnum.BALANCE).currency.subtract(
        price !== undefined ? price : staffMember.marketValue,
        {
          category: BankCategory.TRANSFERS,
          label: `Buy staff member ${staffMember.name}`,
        }
      );
    }

    staffMember.team = team;
    await staffMember.save();

    console.debug('Transfer stuff', staffMember, team);
  }
}